import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Badge,
  Flex,
  useBreakpointValue,
  Tooltip,
  Alert,
  AlertIcon,
  Button,
  useToast,
} from '@chakra-ui/react';
import { 
  FaClock, 
  FaMoneyBillWave,
  FaInfoCircle,
  FaChartLine,
  FaDownload
} from 'react-icons/fa';
import { useWalletContext } from '../../context/WalletContext';

const PendingEarnings = () => {
  const { 
    pendingEarnings, 
    formatCurrency, 
    loading,
    claimEarnings 
  } = useWalletContext();
  
  const [isClaiming, setIsClaiming] = useState(false);
  const toast = useToast();
  
  // Responsive values
  const cardSpacing = useBreakpointValue({ base: 3, md: 4 });
  const showDetails = useBreakpointValue({ base: false, md: true });
  
  // Theme colors
  const cardBg = 'dark.100';
  const borderColor = 'dark.900';
  const hoverBg = 'secondary.500';

  const formatEarningType = (type) => {
    const typeMap = {
      'sale_earning': 'Track Sale',
      'referral_earning': 'Referral Bonus'
    };
    return typeMap[type] || type;
  };

  const getEarningIcon = (type) => {
    return type === 'referral_earning' ? FaChartLine : FaMoneyBillWave;
  };

  const getEarningColor = (type) => {
    return type === 'referral_earning' ? 'purple' : 'green';
  };

  const formatTimeRemaining = (days) => {
    if (days <= 0) return 'Available now';
    if (days === 1) return '1 day remaining';
    return `${days} days remaining`;
  };



  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Text color="gray.400">Loading pending earnings...</Text>
      </Box>
    );
  }

  if (!pendingEarnings || pendingEarnings.length === 0) {
    return (
      <Box p={8} textAlign="center">
        <VStack spacing={4}>
          <Icon as={FaClock} boxSize={12} color="gray.500" />
          <VStack spacing={2}>
            <Text fontSize="lg" fontWeight="bold" color="gray.400">
              No pending earnings
            </Text>
            <Text fontSize="sm" color="gray.500" maxW="300px">
              Your earnings from sales and referrals will appear here during the 5-day clearing period
            </Text>
          </VStack>
        </VStack>
      </Box>
    );
  }

  // Group earnings by availability
  const availableNow = pendingEarnings.filter(earning => earning.days_remaining <= 0);
  const stillPending = pendingEarnings.filter(earning => earning.days_remaining > 0);
  const totalPendingAmount = pendingEarnings.reduce((sum, earning) => sum + earning.amount, 0);
  const totalAvailableAmount = availableNow.reduce((sum, earning) => sum + earning.amount, 0);

  const handleClaimEarnings = async () => {
    if (availableNow.length === 0) {
      toast({
        title: 'No Earnings Available',
        description: 'You have no earnings ready to claim at this time.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsClaiming(true);
      await claimEarnings();
    } catch (error) {
      // Error is already handled in the claimEarnings function
      console.error('Error claiming earnings:', error);
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Header with Summary */}
      <VStack spacing={4}>
        <HStack justify="space-between" w="100%">
          <Text fontSize="lg" fontWeight="bold" color="white">
            Pending Earnings
          </Text>
          <HStack>
            <Tooltip label="Earnings are held for 5 business days for security">
              <Icon as={FaInfoCircle} color="gray.400" cursor="help" />
            </Tooltip>
            <Text fontSize="sm" color="gray.400">
              Total: {formatCurrency(totalPendingAmount)}
            </Text>
          </HStack>
        </HStack>

        {/* Available Now Alert */}
        {availableNow.length > 0 && (
          <Alert status="success" borderRadius="md" bg="primary.100">
            <AlertIcon />
            <Flex justify="space-between" align="center" w="full">
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" fontWeight="bold" color="dark.500">
                  {formatCurrency(totalAvailableAmount)}
                </Text>
                <Text fontSize="xs" color="dark.500">
                  {availableNow.length} earning{availableNow.length > 1 ? 's' : ''} cleared and ready for your wallet
                </Text>
                <Button
                size="sm"
                onClick={handleClaimEarnings}
                isLoading={isClaiming}
                loadingText="Claiming..."
                _hover={{ bg: 'green.600' }}
              >
                Claim Now
              </Button>
              </VStack>
              
            </Flex>
          </Alert>
        )}
      </VStack>

      {/* Earnings List */}
      <VStack spacing={cardSpacing}>
        {/* Available Earnings First */}
        {availableNow.map((earning) => {
          const IconComponent = getEarningIcon(earning.type);
          const color = getEarningColor(earning.type);
          
          return (
            <Box
              key={earning.earning_id}
              p={4}
              bg={cardBg}
              borderRadius="lg"

              w="100%"
              _hover={{ bg: hoverBg }}
            >
              <Flex justify="space-between" align="start" direction={{ base: 'column', md: 'row' }} gap={3}>
                <HStack spacing={3} flex={1}>
                  <Icon as={IconComponent} color={`${color}.400`} boxSize={5} />
                  <VStack align="start" spacing={1} flex={1}>
                    <HStack>
                      <Text fontSize="sm" fontWeight="bold" color="white">
                        {formatEarningType(earning.type)}
                      </Text>
                      <Badge colorScheme="green" size="sm">
                        Available Now
                      </Badge>
                    </HStack>
                    {earning.source_name && (
                      <Text fontSize="xs" color={earning.source === 'merchant' ? 'purple.400' : 'blue.400'} fontWeight="medium">
                        {earning.source_name}
                      </Text>
                    )}
                    {!earning.source_name && earning.artist_name && (
                      <Text fontSize="xs" color="blue.400" fontWeight="medium">
                        Artist: {earning.artist_name}
                      </Text>
                    )}
                    <Text fontSize="xs" color="gray.400">
                      Earned on {formatDate(earning.earned_date)}
                    </Text>
                    {showDetails && (
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {earning.description}
                      </Text>
                    )}
                  </VStack>
                </HStack>
                
                <VStack align="end" spacing={1}>
                  <Text fontSize="md" fontWeight="bold" color="green.400">
                    {formatCurrency(earning.amount)}
                  </Text>
                  <Text fontSize="xs" color="green.300">
                    Ready to transfer
                  </Text>
                </VStack>
              </Flex>
            </Box>
          );
        })}

        {/* Still Pending Earnings */}
        {stillPending.map((earning) => {
          const IconComponent = getEarningIcon(earning.type);
          const color = getEarningColor(earning.type);

          
          return (
            <Box
              key={earning.earning_id}
              p={4}
              bg={cardBg}
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
              w="100%"
              _hover={{ bg: hoverBg }}
            >
              <Flex justify="space-between" align="start" direction={{ base: 'column', md: 'row' }} gap={3}>
                <HStack spacing={3} flex={1}>
                  <Icon as={IconComponent} color={`${color}.400`} boxSize={5} />
                  <VStack align="start" spacing={1} flex={1}>
                    <HStack>
                      <Text fontSize="sm" fontWeight="bold" color="white">
                        {formatEarningType(earning.type)}
                      </Text>
                      <Badge size="sm" variant="subtle">
                        {formatTimeRemaining(earning.days_remaining)}
                      </Badge>
                    </HStack>
                    {earning.source_name && (
                      <Text fontSize="xs" color={earning.source === 'merchant' ? 'purple.400' : 'blue.400'} fontWeight="medium">
                        {earning.source_name}
                      </Text>
                    )}
                    {!earning.source_name && earning.artist_name && (
                      <Text fontSize="xs" color="blue.400" fontWeight="medium">
                        Artist: {earning.artist_name}
                      </Text>
                    )}
                    <Text fontSize="xs" color="gray.400">
                      Earned on {formatDate(earning.earned_date)}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      Available on {formatDate(earning.eligible_date)}
                    </Text>
                    {showDetails && (
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {earning.description}
                      </Text>
                    )}
                  </VStack>
                </HStack>
                
                <VStack align="end" spacing={2} minW="120px">
                  <Text fontSize="md" fontWeight="bold" color={`${color}.400`}>
                    {formatCurrency(earning.amount)}
                  </Text>
                  
     
                </VStack>
              </Flex>
            </Box>
          );
        })}
      </VStack>

      {/* Info Footer */}
      <Box p={4} bg="blue.900" borderRadius="md" border="1px solid" borderColor="blue.600">
        <HStack>
          <Icon as={FaInfoCircle} color="blue.300" />
          <VStack align="start" spacing={1}>
            <Text fontSize="sm" fontWeight="bold" color="blue.100">
              About pending earnings
            </Text>
            <Text fontSize="xs" color="blue.200">
              Earnings are held for 5 business days (excluding weekends and holidays) before becoming 
              available in your wallet. This protects against chargebacks and ensures secure transactions.
            </Text>
          </VStack>
        </HStack>
      </Box>
    </VStack>
  );
};

export default PendingEarnings;